import React, { FC, useEffect, useState } from "react";
import { DEMO_CAR_LISTINGS } from "data/listings";
import { CarDataType } from "data/types";
import { Link } from "react-router-dom";
import NcImage from "shared/NcImage/NcImage";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { getAvatar } from "services/apiServices";
import { useNavigate } from "react-router-dom";
import { Star, Users, Zap } from 'lucide-react';

export interface VanTourCardProps {
    className?: string;
    data?: CarDataType;
    size?: "default" | "small";
}

const DEMO_DATA: CarDataType = DEMO_CAR_LISTINGS[0];

const VanTourCard: FC<VanTourCardProps> = ({
    size = "default",
    className = "",
    data = DEMO_DATA,
}) => {
    const {
        featuredImage,
        title,
        href,
        like,
        saleOff,
        isAds,
        price,
        reviewStart,
        reviewCount,
        seats,
        gearshift,
    } = data;

    const [avatarFile, setAvatarFile] = useState<any>([]);
    const [profileImg, setProfileImg] = useState<any>("");

    const getProfile = async(filename: any) => {
        try {
            const profile: any = await getAvatar(filename);
            setProfileImg(URL.createObjectURL(profile))
        } catch (error) {
            setProfileImg("")
        }
    }

    useEffect(() => {
        if(avatarFile.length === 0){
            getProfile(featuredImage);
        }
    },[featuredImage])

    const navGate = useNavigate()

    const renderSliderGallery = () => {
        return (
            <div className="relative w-full overflow-hidden">
                <div className="aspect-w-16 aspect-h-9 h-48">
                    <NcImage
                        containerClassName="flex items-center justify-center bg-gray-100"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        src={profileImg}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-20"></div>
                </div>
                {/* {saleOff && <SaleOffBadge className="absolute left-3 top-3" />} */}
            </div>
        );
    };

    const renderContent = () => {
        return (
            <div className={`${size === "default" ? "p-6" : "p-4"} space-y-4`}>
                <div className="space-y-2">
                    {saleOff && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-500/10 rounded-full border border-amber-500/20">
                            <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                            <span className="text-xs font-medium text-amber-600">Featured</span>
                        </span>
                    )}
                    <h2 className={`capitalize font-bold text-gray-900 line-clamp-2 leading-tight ${
                        size === "default" ? "text-lg" : "text-base"
                    }`}>
                        {title}
                    </h2>
                </div>

                {(seats || gearshift || price) && (
                    <div className="flex flex-wrap gap-4 py-3 border-t border-b border-gray-200">
                        {seats && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Users className="w-4 h-4 text-amber-600" />
                                <span>{seats} Seats</span>
                            </div>
                        )}
                        {gearshift && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Zap className="w-4 h-4 text-amber-600" />
                                <span>{gearshift}</span>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex items-center justify-between">
                    {reviewStart && (
                        <div className="flex items-center gap-2">
                            <div className="flex gap-0.5">
                                {[...Array(5)].map((_, i) => (
                                    <Star
                                        key={i}
                                        className={`w-3.5 h-3.5 ${
                                            i < Math.round(reviewStart)
                                                ? "fill-amber-500 text-amber-500"
                                                : "text-gray-300"
                                        }`}
                                    />
                                ))}
                            </div>
                            {reviewCount && (
                                <span className="text-xs text-gray-500">({reviewCount})</span>
                            )}
                        </div>
                    )}
                    {price && (
                        <span className="text-lg font-bold text-amber-600">${price}</span>
                    )}
                </div>

                <ButtonPrimary
                    onClick={() => navGate(`/van-tours/view/${data.id}`)}
                    className="w-full rounded-lg py-2.5 font-semibold transition-all duration-300 hover:shadow-lg"
                >
                    View More
                </ButtonPrimary>
            </div>
        );
    };

    return (
        <div
            className={`nc-VanTourCard group relative border border-gray-200 rounded-xl overflow-hidden bg-white shadow-md transition-all duration-300 hover:border-amber-500/50 hover:shadow-xl hover:shadow-amber-100 ${className}`}
            data-nc-id="VanTourCard"
        >
            {renderSliderGallery()}
            {renderContent()}
        </div>
    );
};

export default VanTourCard;
